import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-hot-toast';
import ClockLoader from 'shared/components/ClockLoader';
import { fetchActiveTenantUsers } from 'shared/api/services/tenantUserService';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';
import EntityFormFields from './EntityFormFields';
import { apiErrorMessage } from '../utils/responseHelpers';

function buildSchema(fields, isEdit) {
  const shape = {};
  fields.forEach((field) => {
    if (field.hidden || (isEdit && field.hideOnEdit)) return;
    if (!field.required) return;
    if (field.type === 'boolean') {
      shape[field.name] = yup.boolean();
    } else if (field.type === 'multiEnum' || field.type === 'multiLookupSelect') {
      shape[field.name] = yup.array();
    } else {
      shape[field.name] = yup.string().trim().required(`${field.label} is required`);
    }
  });
  return yup.object(shape);
}

const EditEntityDialog = ({ open, onClose, onSuccess, config, row, updateItem, softwareOptions }) => {
  const [submitting, setSubmitting] = useState(false);
  const [lookupOptions, setLookupOptions] = useState({});
  const validationSchema = useMemo(
    () => buildSchema(config.fields, true),
    [config.fields]
  );
  const initialValues = useMemo(
    () => config.getInitialValues(row, config.fields),
    [config, row]
  );

  useEffect(() => {
    if (!open || !config.loadTenantUsers) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const tenantId = getRegistryTenantIdFromSession();
        const users = await fetchActiveTenantUsers(tenantId);
        if (cancelled) return;
        setLookupOptions({
          tenantUsers: users.map((u) => ({
            id: String(u.id),
            name: u.name || u.email || String(u.id),
            email: u.email || '',
            label: u.email ? `${u.name || 'User'} (${u.email})` : (u.name || String(u.id)),
          })),
        });
      } catch {
        if (!cancelled) setLookupOptions({ tenantUsers: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, config.loadTenantUsers]);

  if (!row) return null;

  const handleSubmit = async (values) => {
    if (config.readOnlyMutations) {
      toast.error(`${config.entityName} catalog is read-only.`);
      return;
    }
    setSubmitting(true);
    try {
      await updateItem(row, values);
      toast.success(`${config.entityName} updated successfully`);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, `Failed to update ${config.entityName.toLowerCase()}.`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
        Edit {config.entityName}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            <DialogContent>
              <EntityFormFields
                fields={config.fields}
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                softwareOptions={softwareOptions}
                lookupOptions={lookupOptions}
                isEdit
                columns={config.formColumns || 1}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="secondary" disabled={submitting || config.readOnlyMutations}>
                {submitting ? <ClockLoader inline size={22} /> : 'Save'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditEntityDialog;
